/**
 * Competency Search Component
 * Subject dropdown and keyword search for finding competencies
 */

import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Search from '@mui/icons-material/Search';
import { CompetencyResult } from '../../types';
import { spineNodesApi } from '../../services/spine.service';
import { useNotificationStore } from '../../store/notificationStore';
import { TagCandidate } from '../../types';

interface CompetencySearchProps {
  onAddTagCandidates: (newTagCandidates: TagCandidate[]) => void;
}

// Local storage key for persisting selected subject
const SELECTED_SUBJECT_KEY = 'spine-tagging-app-selected-subject';

export default function CompetencySearch({ onAddTagCandidates }: CompetencySearchProps) {
  const { showNotification } = useNotificationStore();

  // State for subject selection and search
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [searchTerms, setSearchTerms] = useState<string>('');
  const [searchResults, setSearchResults] = useState<CompetencyResult[]>([]);
  const [selectedCompetencyIds, setSelectedCompetencyIds] = useState<Set<string>>(new Set());
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [creatingTags, setCreatingTags] = useState<boolean>(false);

  // Load persisted subject selection on mount
  useEffect(() => {
    const savedSubject = localStorage.getItem(SELECTED_SUBJECT_KEY);
    if (savedSubject) {
      setSelectedSubject(savedSubject);
    }
  }, []);

  // Persist subject selection changes
  const handleSubjectChange = (newSubject: string) => {
    setSelectedSubject(newSubject);
    localStorage.setItem(SELECTED_SUBJECT_KEY, newSubject);
    // Clear previous search results when subject changes
    setSearchResults([]);
    setSelectedCompetencyIds(new Set());
  };

  // Fetch subjects with caching
  const {
    data: subjectNodes,
    isLoading: subjectNodesLoading,
    error: subjectNodesError,
  } = useQuery({
    queryKey: ['spine-node-subjectNodes'],
    queryFn: spineNodesApi.getSubjects,
    staleTime: 5 * 60 * 1000, // 5-minute cache
  });

  // Parse search terms (comma-separated)
  const parseSearchTerms = (input: string): string[] => {
    return input
      .split(',')
      .map(term => term.trim())
      .filter(term => term.length > 0);
  };

  // Execute competency search
  const handleSearch = async () => {
    if (!selectedSubject || !searchTerms.trim()) {
      showNotification('warning', 'Please select a subject and enter search terms');
      return;
    }

    const keywords = parseSearchTerms(searchTerms);
    if (keywords.length === 0) {
      showNotification('warning', 'Please enter at least one search term');
      return;
    }

    setIsSearching(true);
    try {
      const response = await spineNodesApi.searchCompetencies(selectedSubject, keywords);

      if (response.success) {
        setSearchResults(response.data || []);
        setSelectedCompetencyIds(new Set()); // Clear previous selections
        showNotification('success', `Found ${response.data?.length || 0} competencies`);
      } else {
        setSearchResults([]);
        showNotification('error', response.message || 'Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      showNotification('error', 'Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Toggle competency selection
  const handleCompetencyToggle = (competencyId: string) => {
    const newSelection = new Set(selectedCompetencyIds);
    if (newSelection.has(competencyId)) {
      newSelection.delete(competencyId);
    } else {
      newSelection.add(competencyId);
    }
    setSelectedCompetencyIds(newSelection);
  };

  // Create tag candidates from selected competencies
  const handleCreateTags = async () => {
    if (selectedCompetencyIds.size === 0) {
      showNotification('warning', 'Please select at least one competency');
      return;
    }

    setCreatingTags(true);
    try {
      const competencyIds = Array.from(selectedCompetencyIds);
      const result = await spineNodesApi.createTagCandidates(competencyIds);

      onAddTagCandidates(result.tagCandidates);
      showNotification('success', `${result.count} tag candidates added successfully`);

      // Reset selection after successful creation
      setSelectedCompetencyIds(new Set());
    } catch (error) {
      console.error('Error creating tags:', error);
      showNotification('error', 'Failed to create tag candidates');
    } finally {
      setCreatingTags(false);
    }
  };

  // Highlight search terms in text
  const highlightSearchTerms = (text: string, searchTerms: string): string => {
    const keywords = parseSearchTerms(searchTerms);
    let highlightedText = text;

    keywords.forEach(keyword => {
      const regex = new RegExp(`(${keyword})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
    });

    return highlightedText;
  };

  // Handle keyboard events
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  if (subjectNodesError) {
    return (
      <Alert severity="error">
        Failed to load subjects: {subjectNodesError.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="textSecondary" display="block">
          Search Competencies
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Showing {searchResults.length} competencies
        </Typography>
      </Box>

      {/* Search Controls */}
      <Box sx={{ mb: 2, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
        {/* Subject Dropdown, Search Input, and Submit Button on same line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200, flexShrink: 0 }}>
            <InputLabel size="small">Subject</InputLabel>
            <Select
              size="small"
              value={selectedSubject}
              label="Subject"
              onChange={(e) => handleSubjectChange(e.target.value)}
              disabled={subjectNodesLoading}
            >
              {subjectNodesLoading && (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading subjects...
                </MenuItem>
              )}
              {subjectNodes?.map((subject) => (
                <MenuItem key={subject.nodeId} value={subject.name}>
                  {subject.name}
                  {subject.description && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      ({subject.description})
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="search on comma separated terms..."
            value={searchTerms}
            onChange={(e) => setSearchTerms(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSearching}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isSearching || !selectedSubject || !searchTerms.trim()}
            startIcon={isSearching ? <CircularProgress size={16} /> : null}
            sx={{ flexShrink: 0 }}
          >
            Search
          </Button>
        </Box>
      </Box>

      {/* Search Results */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {searchResults.length === 0 && !isSearching && (
          <Paper sx={{ m: 2, p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No competencies found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try these tips:
            </Typography>
            <Box sx={{ mt: 2, textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Use fewer or more general search terms
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Try different keywords related to the concept
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Use comma-separated terms for multiple keywords
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • Check if the correct subject is selected
              </Typography>
            </Box>
          </Paper>
        )}

        {searchResults.length > 0 && (
          <List>
              {searchResults.map((competency) => (
                <ListItem
                  key={competency.identifier}
                  dense
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedCompetencyIds.has(competency.identifier)}
                      onChange={() => handleCompetencyToggle(competency.identifier)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: highlightSearchTerms(competency.title, searchTerms),
                        }}
                      />
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {competency.domain} • {competency.cluster}
                        </Typography>
                        {competency.subtitle && (
                          <Typography variant="caption" display="block">
                            {competency.subtitle}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
        )}
      </Box>

      {/* Create Tags Button */}
      {selectedCompetencyIds.size > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleCreateTags}
            disabled={creatingTags}
            startIcon={creatingTags ? <CircularProgress size={16} /> : null}
          >
            + Tag ({selectedCompetencyIds.size})
          </Button>
        </Box>
      )}
    </Box>
  );
}